import { ComponentProps } from "react";
import { styled, Text, View } from "tamagui";

import { Card } from "./card";

type ContainerProps = ComponentProps<typeof Card>;

const Container: React.FC<ContainerProps> = ({ children, ...rest }) => {
  return (
    <Card
      paddingHorizontal={"$3"}
      paddingVertical={"$2"}
      variant="borderless"
      backgroundColor={"$warm-snow-500"}
      gap={"$2"}
      {...rest}
    >
      {children}
    </Card>
  );
};

const Title = styled(Text, {
  name: "ChipsMultiselectTitle",
  fontFamily: "$subtitle",
  fontWeight: "$6",
  fontSize: "$4",
  lineHeight: "$4",
  color: "$gray-primary-900",
});

const Description = styled(Text, {
  name: "ChipsMultiselectDescription",
  fontFamily: "$subtitle",
  fontWeight: "$5",
  fontSize: "$2",
  lineHeight: "$2",
  color: "$gray-primary-900",
});

const ChipBase = styled(View, {
  name: "ChipsMultiselectChipBase",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  width: "auto",
  variants: {
    selected: {
      true: {
        borderWidth: "$0.1",
        borderColor: "transparent",
      },
      false: {
        borderWidth: "$0.1",
        backgroundColor: "transparent",
      },
    },

    size: {
      lg: {
        paddingHorizontal: "$3",
        height: "$4",
      },
    },

    rounded: {
      lg: {
        borderRadius: "$5",
      },
    },
  } as const,
  defaultVariants: {
    size: "lg",
    rounded: "lg",
  },
});

const ChipText = styled(Text, {
  name: "ChipText",
  width: "auto",
  color: "white",
  fontFamily: "$body",
  fontWeight: "$4",
  fontSize: "$3",
  lineHeight: "$3",
} as const);

type ChipTextType = typeof ChipText;
type ChipChildrenTypeFunction = (
  val: ChipTextType,
  conf: {
    selected?: boolean;
    backgroundColor?: string;
  },
) => React.ReactNode;
type ChipChildren = React.ReactNode | ChipChildrenTypeFunction;

type ChipProps = Omit<
  ComponentProps<typeof ChipBase>,
  "backgroundColor" | "children"
> & {
  backgroundColor?: string;
  children?: ChipChildren;
};

const isChildrenFunction = (
  child: ChipChildren,
): child is ChipChildrenTypeFunction => typeof child === "function";

const Chip: React.FC<ChipProps> = ({
  children,
  selected,
  backgroundColor,
  ...rest
}) => {
  return (
    <ChipBase
      style={{
        ...(selected && { backgroundColor }),
        ...(!selected && {
          borderColor: backgroundColor,
        }),
      }}
      selected={selected}
      {...rest}
    >
      {isChildrenFunction(children) ? (
        children(ChipText, {
          selected,
          backgroundColor,
        })
      ) : (
        <ChipText
          style={{
            ...(!selected && {
              color: backgroundColor,
            }),
          }}
        >
          {children}
        </ChipText>
      )}
    </ChipBase>
  );
};

export const Chips = {
  Container,
  Title,
  Description,
  Chip,
};
